/* eslint-disable @typescript-eslint/no-explicit-any */
// theme.ts - Fixed theme for the library with no extension capabilities

import { colorGuide, colorPalette, mainColors } from "./colors";
import {
  Typography,
  ColorKey,
  ColorCategory,
  ColorShade,
  SpaceScale,
} from "./theme.types";
import { getSemanticTokens, ThemeMode } from "./semanticTokens";

/**
 * VerticalSpacer scale based on 4px as the base unit
 * Each value is the multiplier of the base unit
 */
export const space = {
  none: 0, // 0px
  "1px": 1, // 1px special case
  micro: 2, // 2px (half unit)
  mini: 4, // 4px (base unit)
  small: 8, // 8px (2 units)
  medium: 12, // 12px (3 units)
  large: 16, // 16px (4 units)
  xl: 20, // 20px (5 units)
  "2xl": 24, // 24px (6 units)
  "3xl": 32, // 32px (8 units)
};

/**
 * Typography scale with detailed information for each type and variant
 */
export const typography = {
  // Font families - primary is used by default
  fontFamilies: {
    primary: "Rubik",
    fallback: "serif",
  },

  // Font weight values
  fontWeights: {
    regular: 400,
    medium: 500,
    semiBold: 600,
    bold: 700,
    extraBold: 800,
  },

  // Typography variants with size, line height, and other properties
  variants: {
    body: {
      1: { fontSize: 16, lineHeight: 24, letterSpacing: 0.2 },
      2: { fontSize: 15, lineHeight: 23, letterSpacing: 0.2 },
      3: { fontSize: 14, lineHeight: 21, letterSpacing: 0.2 },
      4: { fontSize: 13, lineHeight: 20, letterSpacing: 0.2 },
      5: { fontSize: 12, lineHeight: 18, letterSpacing: 0.2 },
      6: { fontSize: 11, lineHeight: 17, letterSpacing: 0.2 },
    },
    heading: {
      0: { fontSize: 44, lineHeight: 55, letterSpacing: 0.2 },
      1: { fontSize: 22, lineHeight: 28, letterSpacing: 0.2 },
      2: { fontSize: 20, lineHeight: 25, letterSpacing: 0.2 },
      3: { fontSize: 18, lineHeight: 23, letterSpacing: 0.2 },
      4: { fontSize: 16, lineHeight: 20, letterSpacing: 0.2 },
      5: { fontSize: 14, lineHeight: 18, letterSpacing: 0.2 },
      6: { fontSize: 13, lineHeight: 16, letterSpacing: 0.2 },
    },
    caps: {
      1: { fontSize: 12, lineHeight: 15, letterSpacing: 2 },
      2: { fontSize: 10, lineHeight: 13, letterSpacing: 2 },
    },
    "serif-heading": {
      1: { fontSize: 24, lineHeight: 30, letterSpacing: 0.2 },
      2: { fontSize: 22, lineHeight: 28, letterSpacing: 0.2 },
      3: { fontSize: 20, lineHeight: 25, letterSpacing: 0.2 },
      4: { fontSize: 18, lineHeight: 23, letterSpacing: 0.2 },
    },
  },
};

/**
 * The fixed theme object for the entire library
 * This is not extensible or customizable by design
 */
export const theme = {
  mainColors,
  colorPalette,
  colorGuide,
  space,
  typography: typography as Typography,
};

/**
 * Resolve a palette reference (e.g., "slate.700") to a hex color value
 */
const resolvePaletteReference = (paletteRef: string): string | null => {
  const parts = paletteRef.split(".");
  if (parts.length === 2) {
    const [category, shade] = parts;
    const colorCategory = category as ColorCategory;
    if (
      colorPalette[colorCategory] &&
      colorPalette[colorCategory][shade as unknown as ColorShade]
    ) {
      return colorPalette[colorCategory][shade as unknown as ColorShade];
    }
  }
  return null;
};

/**
 * Check if a color string is a semantic token
 */
const isSemanticToken = (color: string): boolean => {
  return (
    color.startsWith("background.") ||
    color.startsWith("text.") ||
    color.startsWith("icon.") ||
    color.startsWith("border.")
  );
};

/**
 * Get color value from theme with semantic token support
 *
 * @param color - Color value (semantic token, palette color, main color, or hex)
 * @param theme - Current theme mode (light or dark) for semantic token resolution
 * @returns Resolved hex color value
 *
 * @example
 * // Semantic token (requires theme)
 * getColor("background.neutral.default", "light") // => "#5a5a5a"
 *
 * @example
 * // Palette color (dot notation)
 * getColor("blue.500") // => "#144cc7"
 *
 * @example
 * // Main color
 * getColor("red") // => "#EE4D37"
 *
 * @example
 * // Direct hex
 * getColor("#FF0000") // => "#FF0000"
 */
export const getColor = (
  color: ColorKey | string,
  theme?: ThemeMode
): string => {
  // Check if it's a theme color
  if (typeof color === "string") {
    // Check if it's a semantic token
    if (isSemanticToken(color)) {
      if (!theme) {
        // Default to light theme if no theme provided
        theme = "light";
      }

      const semanticTokens = getSemanticTokens(theme);
      const paletteRef = semanticTokens[color];

      if (paletteRef) {
        // If it starts with #, it's a direct hex value (e.g., utilities colors)
        if (paletteRef.startsWith("#")) {
          return paletteRef;
        }

        // Otherwise, resolve the palette reference
        const resolved = resolvePaletteReference(paletteRef);
        if (resolved) {
          return resolved;
        }
      }
    }

    // Handle dot notation for palette colors (e.g., "blue.500")
    if (color.includes(".")) {
      const resolved = resolvePaletteReference(color);
      if (resolved) {
        return resolved;
      }
    }

    // Check mainColors
    if (mainColors[color as keyof typeof mainColors]) {
      return mainColors[color as keyof typeof mainColors];
    }
  }

  // Return the original value if not found in theme (e.g., hex codes)
  return color as string;
};

/**
 * Legacy getColor without theme parameter for backward compatibility
 * Note: This version cannot resolve semantic tokens
 */
export const getColorLegacy = (color: ColorKey | string): string => {
  return getColor(color);
};

/**
 * Get font family value from theme with fallbacks
 * Creates a proper CSS font-family string with fallbacks
 */
export const getFontFamily = (fontFamily?: string): string => {
  // If no font family provided, use primary with fallback
  if (!fontFamily) {
    return `${typography.fontFamilies.primary}, ${typography.fontFamilies.fallback}`;
  }

  // If it's a theme key, use that value with fallbacks
  if (fontFamily in typography.fontFamilies) {
    const selectedFont =
      typography.fontFamilies[
        fontFamily as keyof typeof typography.fontFamilies
      ];
    // Don't add fallbacks if the selected font is already the fallback
    if (fontFamily === "fallback") {
      return selectedFont;
    }
    return `${selectedFont}, ${typography.fontFamilies.fallback}`;
  }

  // For custom fonts, add primary and fallback as fallbacks
  return `${fontFamily}, ${typography.fontFamilies.primary}, ${typography.fontFamilies.fallback}`;
};

/**
 * Util to convert a value to a CSS size
 * Handles space scale, number, and string
 */
export const convertToCSSSize = (
  value: string | number | SpaceScale | undefined
): string | undefined => {
  if (value === undefined) return undefined;
  //handle space scale
  if (typeof value === "string" && value in space) {
    return `${space[value as SpaceScale]}px`;
  }
  //handle number
  if (typeof value === "number") {
    return `${value}px`;
  }
  //handle string
  return value;
};

// Export the theme for direct consumption
export const wishUITheme = theme;
